import CustomIcon from "components/CustomIcon";
import Image from "next/image";
import { useDispatch } from "react-redux";
import { highlightMistakes, clearHighlight } from "lib/redux/highlightSlice";

interface RepeatedActionVerbsProps {
  verbs: string[];
  verbsWithCounts?: Array<{ verb: string; count: number }>;
}

export const RepeatedActionVerbs = ({
  verbs,
  verbsWithCounts,
}: RepeatedActionVerbsProps) => {
  const dispatch = useDispatch();
  const hasIssues = verbs.length > 0 || (verbsWithCounts && verbsWithCounts.length > 0);

  if (!hasIssues) {
    return null;
  }

  return (
    <div className="relative mt-8 flex flex-col gap-[10px] rounded-xl bg-[#FEE7E4] px-[15px] py-[10px] lg:gap-[16px] lg:px-[20px] lg:py-[14px]">
      <div className="absolute left-4 top-[-16px] flex items-center gap-2 rounded-xl bg-white px-4 py-2 font-bold text-black shadow-md lg:left-8 lg:px-6">
        <Image
          src="/icons/score-check/Announcement.svg"
          height={24}
          width={24}
          alt="icon"
          className="h-4 w-4 lg:h-5 lg:w-5"
        />
        <span className="text-sm font-bold lg:text-base xl:text-lg">
          Repeated Action Verbs
        </span>
      </div>

      <div className="mt-6">
        <ul className="flex list-disc flex-col items-start px-[3px] marker:text-base marker:text-[#92C5FF] sm:px-[5px] sm:marker:text-lg lg:marker:text-2xl">
          {verbsWithCounts
            ? // Use verbsWithCounts if available
              verbsWithCounts.map((item, index) => (
                <li
                  key={index}
                  className="my-[6px] flex w-full items-center gap-2 rounded-lg bg-[#FFDCDC] px-3 py-2 text-xs font-normal text-[#000000] transition-opacity hover:opacity-80 md:text-base lg:my-[6px] lg:gap-4 lg:text-base xl:my-[10px] cursor-pointer"
                  onMouseEnter={() => {
                    dispatch(highlightMistakes({
                      section: 'overall',
                      itemIndex: null,
                      mistakeType: 'actionVerb',
                      texts: [item.verb]
                    }));
                  }}
                  onMouseLeave={() => {
                    dispatch(clearHighlight());
                  }}
                >
                  <div className="h-auto w-auto">
                    <CustomIcon
                      iconName="cross"
                      className="h-3 w-3 lg:h-4 lg:w-4"
                    />
                  </div>
                  <span>
                    {item.verb} -{" "}
                    <span className="ml-2 text-base font-bold text-red-800 lg:text-lg">
                      {item.count}
                    </span>
                  </span>
                </li>
              ))
            : // Fall back to old behavior if verbsWithCounts is not available
              verbs.map((verb, index) => (
                <li
                  key={index}
                  className="my-[6px] pl-2 xl:pl-3 flex w-full items-center gap-2 rounded-lg bg-[#FFDCDC] px-3 py-2 text-xs font-normal text-[#000000] transition-opacity hover:opacity-80 md:text-base lg:my-[6px] lg:gap-4 lg:text-base xl:my-[10px] cursor-pointer"
                  onMouseEnter={() => {
                    dispatch(highlightMistakes({
                      section: 'overall',
                      itemIndex: null,
                      mistakeType: 'actionVerb',
                      texts: [verb]
                    }));
                  }}
                  onMouseLeave={() => {
                    dispatch(clearHighlight());
                  }}
                >
                  {/* <div className="h-auto w-auto">
                    <CustomIcon
                      iconName="cross"
                      className="h-3 w-3 lg:h-4 lg:w-4"
                    />
                  </div> */}
                  <span>{verb}</span>
                </li>
              ))}
        </ul>

        <div className="mt-4 flex flex-col items-center justify-center gap-3">
          <div className="relative h-32 w-32 lg:h-40 lg:w-40">
            <Image
              src="/assets/resumeScore/RepeatedSentence.png"
              alt="Repeated action verbs illustration"
              fill
              className="object-contain"
            />
          </div>
          <p className="text-center text-sm font-semibold text-red-600 lg:text-base">
            Repeated action verbs weaken your resume
          </p>
          <p className="text-center text-xs text-gray-600 lg:text-sm">
            Use diverse action verbs to showcase your varied skills and achievements.
          </p>
        </div>
      </div>
    </div>
  );
};
